import { useHasPermission } from "@dexteel/mesf-core";
import { ChevronRight, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import type { MenuConfig, MenuItemConfig } from "./menuConfig";

interface NavbarDropdownMenuProps {
	menu: MenuConfig;
}

// Delay (ms) before closing the submenu when the mouse leaves either the
// parent MenuItem or the submenu Paper. Gives the user time to traverse the
// horizontal gap between the two without the submenu collapsing under them.
const SUBMENU_CLOSE_DELAY_MS = 220;

/**
 * Recursive sub-menu item — supports the 3-level nested layout from the
 * legacy IHBox navbar (e.g. Reports > Rollshop Operations > {Rolls, Rollsets}).
 *
 * Behavior:
 *   - Leaf (`item.to` set, `item.items` empty): renders a Link MenuItem.
 *   - Parent (`item.items` non-empty): renders a MenuItem with a chevron;
 *     hovering opens a submenu anchored to the right side. The submenu
 *     stays open as long as the cursor is over the parent OR the submenu —
 *     a short close delay covers the gap between them.
 */
const NavbarSubMenuItem = ({
	item,
	onLeafClick,
}: {
	item: MenuItemConfig;
	onLeafClick: () => void;
}) => {
	const hasPermissionTo = useHasPermission();
	const [subAnchor, setSubAnchor] = useState<HTMLElement | null>(null);
	const isOpen = Boolean(subAnchor);
	const closeTimerRef = useRef<number | null>(null);

	const cancelClose = () => {
		if (closeTimerRef.current !== null) {
			window.clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	};

	const scheduleClose = () => {
		cancelClose();
		closeTimerRef.current = window.setTimeout(() => {
			setSubAnchor(null);
			closeTimerRef.current = null;
		}, SUBMENU_CLOSE_DELAY_MS);
	};

	const visibleChildren = (item.items ?? []).filter(
		(c) => !c.permission || hasPermissionTo(c.permission),
	);
	const hasChildren = visibleChildren.length > 0;

	if (!hasChildren) {
		// Leaf — clickable Link
		return (
			<MenuItem
				component={Link}
				to={item.to ?? "#"}
				onClick={onLeafClick}
				sx={{ textDecoration: "none", color: "inherit" }}
			>
				<Typography>{item.label}</Typography>
			</MenuItem>
		);
	}

	// Parent — opens flyout submenu on hover, stays open while traversing
	// to the submenu thanks to the close delay.
	return (
		<>
			<MenuItem
				onMouseEnter={(e: MouseEvent<HTMLLIElement>) => {
					cancelClose();
					setSubAnchor(e.currentTarget);
				}}
				onMouseLeave={scheduleClose}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
				}}
			>
				<Typography>{item.label}</Typography>
				<ChevronRight fontSize="small" />
			</MenuItem>
			<Menu
				anchorEl={subAnchor}
				open={isOpen}
				onClose={() => setSubAnchor(null)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				disableAutoFocus
				disableEnforceFocus
				disableRestoreFocus
				slotProps={{
					paper: {
						onMouseEnter: cancelClose,
						onMouseLeave: scheduleClose,
					},
				}}
				sx={{
					pointerEvents: "none",
					"& .MuiPaper-root": { pointerEvents: "auto" },
				}}
			>
				{visibleChildren.map((child) => (
					<NavbarSubMenuItem
						key={`${item.label}-${child.label}`}
						item={child}
						onLeafClick={() => {
							cancelClose();
							setSubAnchor(null);
							onLeafClick();
						}}
					/>
				))}
			</Menu>
		</>
	);
};

/**
 * Top-level dropdown menu component for the navbar.
 * Renders a button that opens a dropdown with menu items (which may be
 * leaves or parents with nested submenus).
 */
export const NavbarDropdownMenu = ({ menu }: NavbarDropdownMenuProps) => {
	const hasPermissionTo = useHasPermission();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const isOpen = Boolean(anchorEl);

	const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const visibleItems = menu.items.filter(
		(item) => !item.permission || hasPermissionTo(item.permission),
	);

	if (visibleItems.length === 0) {
		return null;
	}

	return (
		<>
			<Button
				aria-controls={`${menu.id}-menu`}
				aria-haspopup="true"
				onClick={handleOpen}
				sx={{
					marginRight: (theme) => theme.spacing(2),
					color: "white",
					display: "flex",
					alignItems: "center",
				}}
			>
				{menu.label}
				{isOpen ? (
					<ExpandLess sx={{ marginLeft: (theme) => theme.spacing(0.5) }} />
				) : (
					<ExpandMore sx={{ marginLeft: (theme) => theme.spacing(0.5) }} />
				)}
			</Button>
			<Menu
				id={`${menu.id}-menu`}
				anchorEl={anchorEl}
				keepMounted
				open={isOpen}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Box>
					{visibleItems.map((item) => (
						<NavbarSubMenuItem
							key={`${menu.id}-${item.label}`}
							item={item}
							onLeafClick={handleClose}
						/>
					))}
				</Box>
			</Menu>
		</>
	);
};
